import { Grid, Text, Button, Link } from '@chakra-ui/react'
import { useTrail } from 'react-spring'
import { useInView } from 'react-intersection-observer'
import NextLink from 'next/link'
import { AnimatedBox, DEFAULT_SPRING_CONFIG } from '@/lib/springComponent'

import IconWidget from './IconWidget'

import { WidgetProps } from '../../types'
import { useTranslation } from 'next-i18next'
import { LEGAL_FORM } from '@/routes/resources'

interface Props {
  items: WidgetProps[]
}

function IconWidgetList({ items }: Props): React.ReactElement {
  const { t } = useTranslation('services')
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const springs = useTrail(items.length, {
    to: {
      transform: inView ? 'translate3d(0, 0px, 0)' : 'translate3d(0, 32px, 0)',
      opacity: inView ? 1 : 0,
    },
    config: DEFAULT_SPRING_CONFIG,
  })

  return (
    <div>
      <Grid
        sx={{
          gridGap: { base: 6, sm: 8 },
          gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
        }}
      >
        <Grid
          ref={ref}
          sx={{
            gridGap: { base: 4, sm: 6 },
            gridTemplateColumns: '1fr',
            display: { base: 'none', md: 'grid' },
          }}
        >
          {items.slice(0, 4).map((item, index) => (
            <AnimatedBox key={`${index}`} style={springs[index]}>
              <IconWidget {...item} index={index + 1} isReverse={true} />
            </AnimatedBox>
          ))}
        </Grid>
        <Grid
          ref={ref}
          sx={{
            gridGap: { base: 4, sm: 6 },
            gridTemplateColumns: '1fr',
            alignContent: 'space-between',
            display: { base: 'none', md: 'grid' },
          }}
        >
          {items.slice(4).map((item, index) => (
            <AnimatedBox key={`${index}`} style={springs[index]}>
              <IconWidget {...item} index={index + 1} />
            </AnimatedBox>
          ))}
        </Grid>

        <Grid
          sx={{
            gridGap: { base: 4, sm: 6 },
            gridTemplateColumns: '1fr',
            display: { base: 'grid', md: 'none' },
          }}
        >
          {items.map((item, index) => (
            <AnimatedBox key={`${index}`} style={springs[index]}>
              <IconWidget {...item} index={index + 1} isReverse={true} />
            </AnimatedBox>
          ))}
        </Grid>
      </Grid>
      <Grid sx={{ paddingTop: '64px', justifyItems: 'center', gap: '24px' }}>
        <Text
          as="h2"
          sx={{
            fontSize: { base: '2xl', sm: '3.5xl' },
            textAlign: 'center',
            fontWeight: 'medium',
          }}
        >
          {t('legal.document.text.download')}
        </Text>
        <NextLink href={LEGAL_FORM.pathname} passHref>
          <Button
            as={Link}
            colorScheme="primary"
            size="xl"
            sx={{
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            {t('legal.document.button.getLegalDocument')}
          </Button>
        </NextLink>
      </Grid>
    </div>
  )
}

export default IconWidgetList
